import { IConversationList, IConversation, IConversationTitle, IReference } from "Common/Interfaces/Domain/Conversations";
import { baseAPI } from "../BaseAPI";
import { IQueryParams } from "Common/Interfaces/Applications/Common/IQueryParams";
import { SERVICE_TAG_TYPES } from "../Constants";
import { IMessage } from "Common/Interfaces/Domain/Conversations/IMessages";
import { USER_MESSAGE_TYPES } from "Common/Constants/UserMessageTypes";
import { IFeedback } from "Common/Interfaces";
import { INFO_TYPE } from "Common/Constants";
import { parseDivisionScope } from "Common/Functions";

const controller = "conversations";

interface IConversationListApiResponse {
  conversations: IConversationTitle[];
  pageNumber: number;
  continuationToken: string | null;
}

interface IMessageApiResponse {
  messageId: string;
  content: string;
  authorRole: string;
  feedbacks: IFeedback[];
}

interface IConversationApiResponse {
  conversationId: string;
  title: string | undefined;
  messages: IMessageApiResponse[];
  references: IReferenceResponse[];
  infoRequested: string;
  divisionScope?: string;
}

interface IReferenceResponse {
  conversationId: string;
  fileId: string;
  fileType: string;
  fileName: string;
  title: string;
  description: string;
  fileUrl: string;
}

const mapAuthorRoleToUserMessageType = (authorRole: string): USER_MESSAGE_TYPES => {
  if (authorRole === "USER") return USER_MESSAGE_TYPES.USER;
  if (authorRole === "ASSISTANT") return USER_MESSAGE_TYPES.ASSISTANT;
  return USER_MESSAGE_TYPES.SYSTEM;
};

const mapInfoRequestedToInfoType = (infoRequested: string): INFO_TYPE => {
  if (infoRequested === "service_history") return INFO_TYPE.SERVICE_HISTORY;
  return INFO_TYPE.DOCUMENT;
};

export const conversationsAPI = baseAPI.injectEndpoints({
  endpoints: (build) => ({
    getConversationHistory: build.query<IConversationList, IQueryParams>({
      query: ({ pageSize, continuationToken }) => {
        const params = new URLSearchParams({
          pageSize: pageSize.toString(),
        });
        if (continuationToken) {
          params.append("continuationToken", continuationToken);
        }
        return {
          url: `${controller}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: IConversationListApiResponse): IConversationList => {
        return {
          conversations: response.conversations ?? [],
          pageSize: response.pageNumber,
          continuationToken: response.continuationToken,
        };
      },
      providesTags: (result) => (result ? [{ type: SERVICE_TAG_TYPES.CONVERSATION_HISTORY }] : []),
    }),

    getConversation: build.query<IConversation, string>({
      query: (conversationId) => ({
        url: `${controller}/${conversationId}`,
        method: "GET",
      }),
      transformResponse: (response: IConversationApiResponse): IConversation => {
        return {
          conversationId: response.conversationId,
          title: response.title,
          messages: response.messages?.map(
            (message: IMessageApiResponse): IMessage => ({
              key: message.messageId,
              text: message.content,
              feedbacks: message.feedbacks,
              user: mapAuthorRoleToUserMessageType(message.authorRole),
            }),
          ),
          references: response.references,
          infoRequested: mapInfoRequestedToInfoType(response.infoRequested),
          divisionScope: parseDivisionScope(response.divisionScope),
        };
      },
      providesTags: [{ type: "CONVERSATION" }],
    }),

    getReferences: build.query<IReference[], string>({
      query: (conversationId) => ({
        url: `${controller}/${conversationId}`,
        method: "GET",
      }),
      transformResponse: (response: IConversationApiResponse): IReference[] => {
        if (!response.references || response.references.length === 0) {
          return [];
        }
        return response.references.map(
          (reference: IReferenceResponse): IReference => ({
            conversationId: response.conversationId,
            fileId: reference.fileId,
            fileType: reference.fileType,
            fileName: reference.fileName,
            title: reference.title,
            description: reference.description,
            fileUrl: reference.fileUrl,
          }),
        );
      },
      providesTags: [{ type: "REFERENCE" }],
    }),
    renameConversation: build.mutation({
      query: ({ id, title }) => ({
        url: `${controller}/${encodeURIComponent(id)}?title=${encodeURIComponent(title)}`,
        method: "PATCH",
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: [SERVICE_TAG_TYPES.CONVERSATION_HISTORY],
    }),
    addFeedback: build.mutation<void, IFeedback>({
      query: ({ conversationId, messageId, thumbs, comment }) => ({
        url: `${controller}/${conversationId}/messages/${messageId}/feedback`,
        method: "POST",
        body: { thumbs, comment },
      }),
    }),
    deleteConversation: build.mutation<void, string>({
      query: (id) => ({
        url: `${controller}/${id}`,
        method: "DELETE",
        body: {},
      }),
      invalidatesTags: [SERVICE_TAG_TYPES.CONVERSATION_HISTORY],
    }),
  }),
});

export const {
  useGetConversationHistoryQuery,
  useGetConversationQuery,
  useAddFeedbackMutation,
  useGetReferencesQuery,
  useRenameConversationMutation,
  useDeleteConversationMutation,
  useLazyGetConversationQuery,
} = conversationsAPI;
